import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AgentDashboardShare, Status } from 'utils/customTypes';
import { RootState, ThunkApi } from 'state/store';
import { SLICE_STATUS } from 'utils/constants';

/* ============================== STATE SETUP =============================== */

interface AgentDashboardSharesState {
  shares: AgentDashboardShare[];
  status: Status;
  addStatus: Status;
  revokeStatus: Status;
}

const initialState: AgentDashboardSharesState = {
  shares: [],
  status: SLICE_STATUS.IDLE,
  addStatus: SLICE_STATUS.IDLE,
  revokeStatus: SLICE_STATUS.IDLE,
};

/* ============================== REDUX THUNK =============================== */

export const getAgentDashboardShares = createAsyncThunk<
  AgentDashboardShare[],
  string,
  ThunkApi
>(
  'normalized/agentDashboardShares/getShares',
  async (artifactId, { extra: { getAgentDashboardApi } }) => {
    return await getAgentDashboardApi().getShares(artifactId);
  }
);

export const addAgentDashboardShare = createAsyncThunk<
  AgentDashboardShare,
  { dashboardId: string; email: string },
  ThunkApi & { rejectValue: number }
>(
  'normalized/agentDashboardShares/addShare',
  async (
    { dashboardId, email },
    { extra: { getAgentDashboardApi }, rejectWithValue }
  ) => {
    try {
      return await getAgentDashboardApi().createShare(dashboardId, email);
    } catch (error) {
      // Surface the HTTP status so the caller can tell a duplicate (409) apart
      // from a generic failure. 0 stands in for a network/unknown error.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      return rejectWithValue(status ?? 0);
    }
  }
);

export const revokeAgentDashboardShare = createAsyncThunk<
  string,
  { dashboardId: string; shareId: string },
  ThunkApi
>(
  'normalized/agentDashboardShares/revokeShare',
  async ({ dashboardId, shareId }, { extra: { getAgentDashboardApi } }) => {
    await getAgentDashboardApi().revokeShare(dashboardId, shareId);
    return shareId;
  }
);

/* ================================= REDUCER ================================ */

const agentDashboardSharesSlice = createSlice({
  name: 'normalized/agentDashboardShares',
  initialState,
  reducers: {
    clearAgentDashboardShares: (state) => {
      state.shares = [];
      state.status = SLICE_STATUS.IDLE;
      state.addStatus = SLICE_STATUS.IDLE;
      state.revokeStatus = SLICE_STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAgentDashboardShares.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getAgentDashboardShares.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getAgentDashboardShares.fulfilled, (state, action) => {
        state.shares = action.payload;
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(addAgentDashboardShare.pending, (state) => {
        state.addStatus = SLICE_STATUS.LOADING;
      })
      .addCase(addAgentDashboardShare.rejected, (state) => {
        state.addStatus = SLICE_STATUS.FAILED;
      })
      .addCase(addAgentDashboardShare.fulfilled, (state, action) => {
        state.addStatus = SLICE_STATUS.SUCCESS;
        // The list endpoint returns shares newest-first, so a freshly created
        // share belongs at the front.
        state.shares.unshift(action.payload);
      })
      .addCase(revokeAgentDashboardShare.pending, (state) => {
        state.revokeStatus = SLICE_STATUS.LOADING;
      })
      .addCase(revokeAgentDashboardShare.rejected, (state) => {
        state.revokeStatus = SLICE_STATUS.FAILED;
      })
      .addCase(revokeAgentDashboardShare.fulfilled, (state, action) => {
        state.revokeStatus = SLICE_STATUS.SUCCESS;
        state.shares = state.shares.filter(
          (share) => share.id !== action.payload
        );
      });
  },
});

/* ================================ ACTIONS ================================= */

export const { clearAgentDashboardShares } = agentDashboardSharesSlice.actions;

/* =============================== SELECTORS ================================ */

const selectShares = (state: RootState) =>
  state.normalized.agentDashboardShares.shares;

const selectStatus = (state: RootState) =>
  state.normalized.agentDashboardShares.status;

const selectAddStatus = (state: RootState) =>
  state.normalized.agentDashboardShares.addStatus;

const selectRevokeStatus = (state: RootState) =>
  state.normalized.agentDashboardShares.revokeStatus;

export const agentDashboardSharesSelectors = {
  selectShares,
  selectStatus,
  selectAddStatus,
  selectRevokeStatus,
};

export default agentDashboardSharesSlice.reducer;
